import React, { useState, useEffect } from "react";
import TwoFactorService from "../../services/twoFactorService";
import { notifySuccess, notifyError } from "../ToastContext";

const TwoFactorModal = () => {
  const [viewState, setViewState] = useState("STATUS"); // STATUS, SETUP, SUCCESS, DISABLE
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Setup state
  const [secretKey, setSecretKey] = useState("");
  const [otpAuthUri, setOtpAuthUri] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Disable state
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const loadStatus = async () => {
    try {
      setLoading(true);
      const res = await TwoFactorService.getStatus();
      if (res?.data?.Status) {
        setIsEnabled(res.data.IsEnabled);
      }
    } catch {
      // Ignored if user not logged in yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const resetModal = () => {
    setViewState("STATUS");
    setVerifyCode("");
    setDisablePassword("");
    setDisableCode("");
    loadStatus();
  };

  const handleStartSetup = async () => {
    try {
      setLoading(true);
      const res = await TwoFactorService.setupInit();
      if (res?.data?.Status) {
        setSecretKey(res.data.SecretKey);
        setOtpAuthUri(res.data.OtpAuthUri);
        setViewState("SETUP");
      } else {
        notifyError(res?.data?.Message || "Failed to initialize 2FA.");
      }
    } catch {
      notifyError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async () => {
    if (!verifyCode || verifyCode.trim().length !== 6) {
      notifyError("Please enter a valid 6-digit code.");
      return;
    }

    try {
      setLoading(true);
      const res = await TwoFactorService.setupConfirm(verifyCode.trim());
      if (res?.data?.Status) {
        setRecoveryCodes(res.data.RecoveryCodes || []);
        setIsEnabled(true);
        notifySuccess("Two-Factor Authentication activated!");
        setViewState("SUCCESS");
      } else {
        notifyError(res?.data?.Message || "Invalid 6-digit code.");
      }
    } catch {
      notifyError("Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword || !disableCode) {
      notifyError("Password and 6-digit code are required.");
      return;
    }

    try {
      setLoading(true);
      const res = await TwoFactorService.disable(
        disablePassword,
        disableCode.trim(),
      );
      if (res?.data?.Status) {
        setIsEnabled(false);
        notifySuccess("Two-Factor Authentication disabled.");
        setViewState("STATUS");
      } else {
        notifyError(res?.data?.Message || "Failed to disable 2FA.");
      }
    } catch {
      notifyError("Failed to disable 2FA.");
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (recoveryCodes && recoveryCodes.length > 0) {
      navigator.clipboard.writeText(recoveryCodes.join("\n"));
      notifySuccess("Recovery codes copied to clipboard!");
    }
  };

  const qrCodeUrl = otpAuthUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpAuthUri)}`
    : "";

  return (
    <div>
      <div
        className="modal fade"
        id="twoFactorModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="twoFactorModalLabel"
        aria-hidden="true"
      >
        <div
          className="modal-dialog"
          style={{ maxWidth: "420px", width: "95%", margin: "0.5rem auto" }}
        >
          <div className="modal-content">
            <div className="modal-header bg-dark text-white">
              <h6
                className="modal-title m-0 text-white"
                id="twoFactorModalLabel"
              >
                <i className="fas fa-shield-alt mr-2 text-warning" /> Two-Factor
                Authentication
              </h6>
              <button
                className="close text-white"
                type="button"
                data-dismiss="modal"
                aria-label="Close"
                onClick={resetModal}
              >
                <span aria-hidden="true" className="text-white">
                  ×
                </span>
              </button>
            </div>

            <div className="modal-body p-4">
              {/* VIEW 1: STATUS */}
              {viewState === "STATUS" && (
                <div>
                  <div
                    className="d-flex align-items-center justify-content-between p-2 mb-3"
                    style={{
                      background: "#f8f9fc",
                      borderRadius: "4px",
                      border: "1px solid #e3e6f0",
                    }}
                  >
                    <span style={{ fontSize: "14px", fontWeight: "600" }}>
                      Status:
                    </span>
                    {isEnabled ? (
                      <span
                        className="badge badge-success px-2 py-1"
                        style={{ fontSize: "12px" }}
                      >
                        Enabled 🟢
                      </span>
                    ) : (
                      <span
                        className="badge badge-secondary px-2 py-1"
                        style={{ fontSize: "12px" }}
                      >
                        Disabled ⚪
                      </span>
                    )}
                  </div>

                  <p style={{ fontSize: "13px", color: "#6e707e" }}>
                    {isEnabled
                      ? "Your subadmin account is secured with Two-Factor Authentication. You will be prompted for your authenticator app code during login."
                      : "Protect your subadmin account and permissions from unauthorized access by requiring a 6-digit Google Authenticator code."}
                  </p>

                  <div className="text-center mt-4">
                    {isEnabled ? (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setViewState("DISABLE")}
                        disabled={loading}
                      >
                        Turn Off 2FA
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm px-4"
                        onClick={handleStartSetup}
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Turn On 2FA"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* VIEW 2: SETUP (QR CODE + 6 DIGIT CODE) */}
              {viewState === "SETUP" && (
                <div>
                  <div className="text-center mb-2">
                    <p
                      style={{
                        fontSize: "12px",
                        margin: "0",
                        color: "#5a5c69",
                      }}
                    >
                      1. Scan with <strong>Google Authenticator</strong>:
                    </p>
                    {qrCodeUrl && (
                      <div className="my-2">
                        <img
                          src={qrCodeUrl}
                          alt="2FA QR Code"
                          style={{
                            width: "160px",
                            height: "160px",
                            border: "1px solid #dddfeb",
                            borderRadius: "4px",
                          }}
                        />
                      </div>
                    )}
                    <div style={{ fontSize: "11px", color: "#858796" }}>
                      Manual Key:{" "}
                      <code className="text-danger">{secretKey}</code>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#4e73df",
                      }}
                    >
                      2. Enter 6-Digit Code from App:
                    </label>
                    <input
                      type="text"
                      maxLength="6"
                      className="form-control text-center"
                      placeholder="000000"
                      style={{
                        fontSize: "20px",
                        letterSpacing: "4px",
                        fontWeight: "bold",
                      }}
                      value={verifyCode}
                      onChange={(e) =>
                        setVerifyCode(e.target.value.replace(/[^0-9]/g, ""))
                      }
                    />
                  </div>

                  <div className="d-flex justify-content-between mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setViewState("STATUS")}
                      disabled={loading}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={handleConfirmSetup}
                      disabled={loading || verifyCode.length !== 6}
                    >
                      {loading ? "Verifying..." : "Verify & Activate"}
                    </button>
                  </div>
                </div>
              )}

              {/* VIEW 3: SUCCESS & BACKUP CODES */}
              {viewState === "SUCCESS" && (
                <div>
                  <div className="text-center mb-2">
                    <h5
                      className="text-success m-0"
                      style={{ fontSize: "16px" }}
                    >
                      🎉 2FA Activated!
                    </h5>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6e707e",
                        marginTop: "6px",
                      }}
                    >
                      Save these single-use{" "}
                      <strong>emergency recovery codes</strong>.
                    </p>
                  </div>

                  <div
                    className="p-2 mb-3"
                    style={{
                      background: "#eaecf4",
                      borderRadius: "4px",
                      maxHeight: "120px",
                      overflowY: "auto",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      textAlign: "center",
                    }}
                  >
                    {recoveryCodes.map((code, idx) => (
                      <div key={idx} style={{ padding: "2px 0" }}>
                        {code}
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-outline-dark btn-sm"
                      onClick={copyRecoveryCodes}
                    >
                      Copy Codes
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      data-dismiss="modal"
                      onClick={resetModal}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {/* VIEW 4: DISABLE 2FA */}
              {viewState === "DISABLE" && (
                <div>
                  <p style={{ fontSize: "12px", color: "#e74a3b" }}>
                    Enter your account password and the current 6-digit code to
                    turn off 2FA:
                  </p>
                  <div className="form-group mb-2">
                    <label style={{ fontSize: "12px" }}>Password</label>
                    <input
                      type="password"
                      className="form-control form-control-sm"
                      placeholder="Current Password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label style={{ fontSize: "12px" }}>6-Digit Code</label>
                    <input
                      type="text"
                      maxLength="6"
                      className="form-control form-control-sm text-center"
                      placeholder="000000"
                      style={{ fontSize: "16px", letterSpacing: "2px" }}
                      value={disableCode}
                      onChange={(e) =>
                        setDisableCode(e.target.value.replace(/[^0-9]/g, ""))
                      }
                    />
                  </div>

                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setViewState("STATUS")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={handleDisable}
                      disabled={
                        loading || !disablePassword || disableCode.length !== 6
                      }
                    >
                      {loading ? "Disabling..." : "Confirm Disable"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;
